import { DIVISIONS } from '../types';
import type { Words } from '../copy';
import type { State } from '../state/session';

export interface PanelHandlers {
  patch: (patch: Partial<State>) => void;
  onQuantize: () => void;
  onDenoise: () => void;
  onExport: () => void;
  onRepair: () => void;
  onCut: () => void;
  onNormalize: () => void;
  onUndo: () => void;
  onDetect: () => void;
  onHalve: () => void;
  onDouble: () => void;
  onTap: () => void;
  onSnapSelection: (edge: 'start' | 'end') => void;
  onMode: (m: 'troubadour' | 'maestro') => void;
  onRetryServer: () => void;
}

interface Props {
  s: State;
  w: Words;
  h: PanelHandlers;
  busy: boolean;
}

export default function SidePanel({ s, w, h, busy }: Props) {
  const offline = s.serverOk === false;
  const noServer = offline || busy;
  const hasSel = !!s.selection && s.selection.end - s.selection.start > 0.05;
  const bpm = s.bpm || s.analysis?.bpm || 0;

  if (s.status === 'empty') {
    return (
      <div className="stack">
        {offline && <OfflineNotice onRetry={h.onRetryServer} />}
        <div className="label">Pour commencer</div>
        <div className="note">
          Enregistre au micro ou importe un fichier. Tout reste sur ton appareil jusqu’à ce que tu
          lances un traitement.
        </div>
        <hr className="hr" />
        <div className="note">
          Le calage sur le tempo et le nettoyage se font sur le serveur Quantime. Le découpage en
          boucles et l’export fonctionnent même hors ligne.
        </div>
      </div>
    );
  }

  if (s.mode === 'troubadour') {
    return (
      <div className="stack">
        {offline && <OfflineNotice onRetry={h.onRetryServer} />}
        <div className="label">Que veux-tu faire&nbsp;?</div>

        <button className="action primary" onClick={h.onQuantize} disabled={noServer || !bpm}>
          <span className="t">⚡ {w.quantizeAction}</span>
          <span className="d">
            {w.quantizeDesc}
            {bpm ? ` — ${bpm.toFixed(1).replace('.', ',')} ${w.bpmUnit}` : ' — tempo à trouver'}
          </span>
        </button>

        <button className="action" onClick={h.onDenoise} disabled={noServer}>
          <span className="t">🧹 {w.denoiseAction}</span>
          <span className="d">{w.denoiseDesc}</span>
        </button>

        <div className="slider">
          <span className="ends">{w.softEnd}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(s.nrAmount * 100)}
            onChange={(e) => h.patch({ nrAmount: Number(e.target.value) / 100 })}
            aria-label={`${w.denoiseAction} — ${w.strength}`}
          />
          <span className="ends">{w.hardEnd}</span>
        </div>

        <button className="action green" onClick={h.onExport}>
          <span className="t">⬇ {w.exportAction}</span>
          <span className="d">{w.exportDesc}</span>
        </button>

        {s.history.length > 0 && (
          <button className="btn block" onClick={h.onUndo}>
            ↩ Annuler la dernière action
          </button>
        )}

        <hr className="hr" />
        <button className="btn dashed block" onClick={() => h.onMode('maestro')}>
          ▸ {w.advanced}
        </button>
        <div className="note">{w.tip}</div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 16 }}>
      {offline && <OfflineNotice onRetry={h.onRetryServer} />}

      <div className="group">
        <div className="group-head">
          <div className="label">Tempo &amp; grille</div>
          <div className="meta">
            {s.quantMeta
              ? `offset ${s.quantMeta.grid_offset.toFixed(3)} · ${s.quantMeta.moves} moves`
              : s.analysis?.engine
                ? s.analysis.engine
                : ''}
          </div>
        </div>
        <div className="row">
          <input
            className="field num"
            type="number"
            step={0.1}
            min={30}
            max={300}
            value={s.bpm ? Number(s.bpm.toFixed(1)) : ''}
            placeholder="BPM"
            onChange={(e) => h.patch({ bpm: Number(e.target.value) })}
            aria-label="BPM"
          />
          <button className="btn sm" onClick={h.onHalve}>÷2</button>
          <button className="btn sm" onClick={h.onDouble}>×2</button>
          <button className="btn sm" onClick={h.onTap}>Tap</button>
          <button className="btn sm" onClick={h.onDetect} disabled={noServer} style={{ flex: 1 }}>
            Détecter
          </button>
        </div>
        <div className="row">
          <select
            className="field"
            style={{ flex: 1 }}
            value={String(s.division)}
            onChange={(e) => h.patch({ division: Number(e.target.value) })}
            aria-label="Division de la grille"
          >
            {DIVISIONS.map((d) => (
              <option key={d.label} value={String(d.value)}>
                Grille {d.label}
              </option>
            ))}
          </select>
          <button className="btn sm" onClick={() => h.onSnapSelection('start')} disabled={!hasSel}>
            ⇤
          </button>
          <button className="btn sm" onClick={() => h.onSnapSelection('end')} disabled={!hasSel}>
            ⇥
          </button>
        </div>
      </div>

      <div className="group">
        <div className="label">Quantification</div>
        <div className="slider">
          <span>{w.strength}</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(s.strength * 100)}
            onChange={(e) => h.patch({ strength: Number(e.target.value) / 100 })}
          />
          <span className="val">{Math.round(s.strength * 100)}%</span>
        </div>
        <select
          className="field"
          value={s.quantMode}
          onChange={(e) => h.patch({ quantMode: e.target.value as 'warp' | 'slice' })}
          aria-label="Mode de quantification"
        >
          <option value="warp">Warp (fluide)</option>
          <option value="slice">Slice (sec)</option>
        </select>
        <label className="check">
          <input
            type="checkbox"
            checked={s.selectionOnly}
            onChange={(e) => h.patch({ selectionOnly: e.target.checked })}
            disabled={!hasSel}
          />
          sélection seule
        </label>
        <button className="btn primary block" onClick={h.onQuantize} disabled={noServer || !bpm}>
          ⚡ Quantifier
        </button>
      </div>

      <div className="group">
        <div className="label">Débruitage</div>
        <div className="slider">
          <span>Amount</span>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(s.nrAmount * 100)}
            onChange={(e) => h.patch({ nrAmount: Number(e.target.value) / 100 })}
          />
          <span className="val">{Math.round(s.nrAmount * 100)}%</span>
        </div>
        <div className="row" style={{ gap: 14 }}>
          <label className="check">
            <input
              type="checkbox"
              checked={s.useHighpass}
              onChange={(e) => h.patch({ useHighpass: e.target.checked })}
            />
            HP 70 Hz
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={s.useHum}
              onChange={(e) => h.patch({ useHum: e.target.checked })}
            />
            Hum 50 Hz
          </label>
        </div>
        <button className="btn block" onClick={h.onDenoise} disabled={noServer}>
          Appliquer le débruitage
        </button>
      </div>

      <div className="group">
        <div className="group-head">
          <div className="label">Édition</div>
          <div className="meta">
            {hasSel && s.selection
              ? `sél. ${s.selection.start.toFixed(2)} → ${s.selection.end.toFixed(2)} s`
              : 'aucune sélection'}
          </div>
        </div>
        <div className="row2">
          <button className="btn sm" onClick={h.onRepair} disabled={!hasSel}>🩹 Réparer</button>
          <button className="btn sm" onClick={h.onCut} disabled={!hasSel}>✂ Couper</button>
          <button className="btn sm" onClick={h.onNormalize}>Normaliser</button>
          <button className="btn sm" onClick={h.onUndo} disabled={!s.history.length}>↩ Annuler</button>
        </div>
      </div>

      <button className="btn green block" onClick={h.onExport}>
        ⬇ Exporter WAV
      </button>

      <button className="btn ghost" onClick={() => h.onMode('troubadour')} style={{ justifyContent: 'flex-start' }}>
        ◂ {w.backSimple}
      </button>
      <div className="note">{w.tipPro}</div>
    </div>
  );
}

function OfflineNotice(p: { onRetry: () => void }) {
  return (
    <div className="alert">
      <div className="t">Le serveur ne répond pas</div>
      <div className="d">
        Tu peux toujours écouter, découper et exporter. Le calage sur le tempo et le nettoyage se
        font sur le serveur — ils reviendront dès la reconnexion.
      </div>
      <button className="btn sm" onClick={p.onRetry} style={{ color: 'var(--red-soft)', borderColor: 'rgba(255,77,94,.5)', background: 'transparent' }}>
        Réessayer
      </button>
    </div>
  );
}
